import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertCircle, ClipboardCheck, History, Save, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { saveItemInspection, getAssetLastInspection } from '../lib/api';

/**
 * ItemInspectionSection
 * ---------------------
 * Renders a mandatory inspection checklist for a single DN item, adapted to
 * the item's asset kind (vehicle vs equipment). Every field except free-form
 * notes is required; the parent uses `onValidityChange(isComplete)` to gate
 * the Finalize / Mark-as-Delivered button.
 *
 * When an asset_id is present we also fetch the last known condition report
 * (`GET /delivery-notes-v2/asset/{asset_id}/last-inspection`) and render it
 * as a "Previous Condition Report" banner so the driver can verify existing
 * damage vs new issues before submitting.
 */
export const VEHICLE_REQUIRED = [
  'km_reading', 'fuel_level', 'wipers_working',
  'fire_extinguisher_present', 'oil_level_ok', 'tires_ok',
  'lights_ok', 'general_condition',
];
export const EQUIPMENT_REQUIRED = [
  'engine_hours', 'fuel_level', 'hydraulic_oil_ok',
  'safety_devices_ok', 'general_condition',
];

const FUEL_LEVELS = [
  { value: 'empty', label: 'Empty (E)' },
  { value: 'quarter', label: '¼ Tank' },
  { value: 'half', label: '½ Tank' },
  { value: 'three_quarter', label: '¾ Tank' },
  { value: 'full', label: 'Full (F)' },
];
const CONDITIONS = [
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
];

const isFilled = (v) => v !== undefined && v !== null && v !== '';

const missingFields = (insp, required) => required.filter((f) => !isFilled(insp?.[f]));

const YesNo = ({ label, value, onChange, testId }) => (
  <div className="rounded-md border p-2 bg-white">
    <Label className="text-xs font-medium">{label} *</Label>
    <div className="flex gap-2 mt-1">
      {[
        { v: true, txt: 'Yes' },
        { v: false, txt: 'No' },
      ].map((o) => {
        const selected = value === o.v;
        return (
          <button
            key={o.txt}
            type="button"
            data-testid={`${testId}-${o.txt.toLowerCase()}`}
            onClick={() => onChange(o.v)}
            className={`px-3 py-1 text-xs rounded border transition ${
              selected
                ? o.v
                  ? 'bg-emerald-600 border-emerald-700 text-white'
                  : 'bg-red-600 border-red-700 text-white'
                : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
            }`}
          >
            {o.txt}
          </button>
        );
      })}
    </div>
  </div>
);

const ItemInspectionSection = ({
  dnId,
  item,
  index,
  category: categoryProp,
  onInspectionChange,
  onValidityChange,
}) => {
  const inferred = item?.asset_kind
    || (categoryProp)
    || (String(item?.asset_type || '').toLowerCase().match(/(excavator|scissor|lift|generator|loader|forklift|crane|compressor|roller|grader|backhoe|bulldozer|genset|manlift|boom|dozer|welder)/) ? 'equipment' : 'vehicle');
  const required = inferred === 'equipment' ? EQUIPMENT_REQUIRED : VEHICLE_REQUIRED;
  const insp = item?.inspection || {};
  const [prev, setPrev] = useState(null);
  const [saving, setSaving] = useState(false);

  const missing = missingFields(insp, required);
  const complete = missing.length === 0;

  // Only notify parent when the completeness status actually flips —
  // never on every parent re-render (which would loop when the parent
  // stores completeness in state and re-passes a fresh callback).
  const prevCompleteRef = useRef(null);
  useEffect(() => {
    if (prevCompleteRef.current === complete) return;
    prevCompleteRef.current = complete;
    if (typeof onValidityChange === 'function') {
      onValidityChange(complete);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complete]);

  // Load previous condition report (best-effort) — depend only on the
  // asset id so the fetch does not re-fire on every parent re-render.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!item?.asset_id) return;
      try {
        const r = await getAssetLastInspection(item.asset_id);
        if (!cancelled && r.data && r.data.source && r.data.source !== 'none') setPrev(r.data);
      } catch { /* ignore */ }
    };
    load();
    return () => { cancelled = true; };
  }, [item?.asset_id]);

  const set = useCallback((patch) => {
    if (typeof onInspectionChange === 'function') {
      onInspectionChange({ ...insp, ...patch });
    }
  }, [insp, onInspectionChange]);

  const handleSave = useCallback(async () => {
    if (!dnId || !item?.item_id) {
      toast.message('Inspection is stored locally until the DN is saved');
      return;
    }
    try {
      setSaving(true);
      await saveItemInspection(dnId, item.item_id, insp);
      toast.success('Inspection saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save inspection');
    } finally {
      setSaving(false);
    }
  }, [dnId, item?.item_id, insp]);

  return (
    <Card className={`border-2 ${complete ? 'border-emerald-300 bg-emerald-50/30' : 'border-amber-300 bg-amber-50/30'}`}
      data-testid={`item-inspection-${index}`}>
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-slate-700" />
            <span className="text-sm font-semibold">
              Inspection Checklist · {inferred === 'equipment' ? 'Equipment' : 'Vehicle'}
            </span>
            {complete ? (
              <Badge className="bg-emerald-600 text-white">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Complete
              </Badge>
            ) : (
              <Badge className="bg-amber-500 text-white" data-testid={`inspection-missing-count-${index}`}>
                {missing.length} field(s) missing
              </Badge>
            )}
          </div>
          {dnId && item?.item_id && (
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="text-xs px-2 py-1 rounded border bg-white hover:bg-slate-50 flex items-center gap-1"
              data-testid={`save-inspection-${index}`}
            >
              <Save className="h-3 w-3" /> {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>

        {/* Previous Condition Report */}
        {prev && (
          <div className="rounded-md border border-blue-300 bg-blue-50 p-2 text-xs" data-testid={`prev-condition-${index}`}>
            <div className="flex items-center gap-1 font-medium text-blue-900 mb-1">
              <History className="h-3.5 w-3.5" />
              Previous Condition Report ({prev.source === 'demobilization' ? 'last demobilization' : `DN ${prev.dn_number}`})
            </div>
            {prev.return_condition && (
              <div><span className="font-medium">Return condition:</span> {prev.return_condition}</div>
            )}
            {prev.condition_notes && (
              <div><span className="font-medium">Notes:</span> {prev.condition_notes}</div>
            )}
            {prev.inspection?.condition_notes && (
              <div><span className="font-medium">Notes:</span> {prev.inspection.condition_notes}</div>
            )}
            {prev.return_hours != null && (
              <div><span className="font-medium">Return hours:</span> {prev.return_hours}</div>
            )}
            <div className="text-[10px] text-blue-700 mt-1">
              Verify existing damage vs new issues before submitting.
            </div>
          </div>
        )}

        {!complete && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-xs flex gap-2 items-start">
            <AlertCircle className="h-3.5 w-3.5 text-amber-700 shrink-0 mt-0.5" />
            <span>Every field below is required for compliance. Complete all items before finalizing delivery.</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {inferred === 'vehicle' && (
            <>
              <div>
                <Label className="text-xs">KM Reading *</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  value={insp.km_reading ?? ''}
                  onChange={(e) => set({ km_reading: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                  placeholder="Odometer"
                  data-testid={`insp-km-${index}`}
                />
              </div>
              <YesNo label="Wipers Working" value={insp.wipers_working} onChange={(v) => set({ wipers_working: v })} testId={`insp-wipers-${index}`} />
              <YesNo label="Fire Extinguisher Present" value={insp.fire_extinguisher_present} onChange={(v) => set({ fire_extinguisher_present: v })} testId={`insp-fire-${index}`} />
              <YesNo label="Oil Level OK" value={insp.oil_level_ok} onChange={(v) => set({ oil_level_ok: v })} testId={`insp-oil-${index}`} />
              <YesNo label="Tires OK" value={insp.tires_ok} onChange={(v) => set({ tires_ok: v })} testId={`insp-tires-${index}`} />
              <YesNo label="Lights OK" value={insp.lights_ok} onChange={(v) => set({ lights_ok: v })} testId={`insp-lights-${index}`} />
            </>
          )}
          {inferred === 'equipment' && (
            <>
              <div>
                <Label className="text-xs">Current Engine Hours *</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  value={insp.engine_hours ?? ''}
                  onChange={(e) => set({ engine_hours: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                  placeholder="Hour meter reading"
                  data-testid={`insp-engine-hours-${index}`}
                />
              </div>
              <YesNo label="Hydraulic Oil Check" value={insp.hydraulic_oil_ok} onChange={(v) => set({ hydraulic_oil_ok: v })} testId={`insp-hyd-${index}`} />
              <YesNo label="Safety Devices OK" value={insp.safety_devices_ok} onChange={(v) => set({ safety_devices_ok: v })} testId={`insp-safety-${index}`} />
            </>
          )}
          <div>
            <Label className="text-xs">Fuel Level *</Label>
            <Select value={insp.fuel_level || ''} onValueChange={(v) => set({ fuel_level: v })}>
              <SelectTrigger data-testid={`insp-fuel-${index}`}><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {FUEL_LEVELS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">General Condition *</Label>
            <Select value={insp.general_condition || ''} onValueChange={(v) => set({ general_condition: v })}>
              <SelectTrigger data-testid={`insp-condition-${index}`}><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {CONDITIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-xs">Condition Notes (optional)</Label>
          <Textarea
            value={insp.condition_notes || ''}
            onChange={(e) => set({ condition_notes: e.target.value })}
            placeholder="Existing scratches, dents, or anything to flag…"
            rows={2}
            data-testid={`insp-notes-${index}`}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default ItemInspectionSection;
